import { r, s } from '@/theme/tokens';
import { Text, Tooltip } from '@/ui';
import { Box, MuiPieChart, useTheme } from '@/ui/primitives';
import type { Theme } from '@/ui/primitives';
import type { SuiteExecutionStatus } from '@flowqa/shared';
import type { ReactElement } from 'react';

type SuiteLatestStatusPieProps = {
  segments: Array<{ name: string; value: number; status: SuiteExecutionStatus | 'untested' }>;
  size: number;
  resolveSliceColor: (theme: Theme, status: SuiteExecutionStatus | 'untested') => string;
};

export const SuiteLatestStatusPie = ({
  segments,
  size,
  resolveSliceColor,
}: SuiteLatestStatusPieProps): ReactElement | null => {
  const theme = useTheme();
  if (segments.length === 0) return null;
  const total = segments.reduce((sum, slice) => sum + slice.value, 0);
  const series = [
    {
      data: segments.map((slice, index) => ({
        id: index,
        value: slice.value,
        label: slice.name,
        color: resolveSliceColor(theme, slice.status),
      })),
      innerRadius: 0,
      outerRadius: '80%',
    },
  ];
  const tooltipContent = (
    <Box sx={{ color: theme.app.colors.text, width: 'fit-content' }}>
      <Box sx={{ pt: '0px', pb: '0px' }}>
        {segments.map((slice, index) => {
          const percentage = total > 0 ? Math.round((slice.value / total) * 100) : 0;
          const rowPaddingTop = index === 0 ? s('sm') : s('xxs');
          const rowPaddingBottom = index === segments.length - 1 ? s('xs') : s('xxs');
          return (
            <Box
              key={slice.name}
              sx={{
                display: 'grid',
                gridTemplateColumns: '16px 1fr auto auto',
                alignItems: 'center',
                columnGap: '4px',
                pl: '1px',
                pr: '4px',
                pt: rowPaddingTop,
                pb: rowPaddingBottom,
              }}
            >
              <Box
                sx={{
                  width: '8px',
                  height: '8px',
                  borderRadius: r('sm'),
                  backgroundColor: resolveSliceColor(theme, slice.status),
                  justifySelf: 'center',
                }}
              />
              <Text
                role="body"
                sx={{
                  color: 'inherit',
                  fontSize: theme.typography.body2.fontSize,
                  lineHeight: theme.typography.body2.lineHeight,
                }}
              >
                {slice.name}
              </Text>
              <Text
                role="body"
                sx={{
                  color: 'inherit',
                  textAlign: 'right',
                  fontSize: theme.typography.body2.fontSize,
                  lineHeight: theme.typography.body2.lineHeight,
                }}
              >
                {slice.value.toLocaleString()}
              </Text>
              <Text
                role="body"
                sx={{
                  color: 'inherit',
                  textAlign: 'right',
                  minWidth: '40px',
                  fontSize: theme.typography.body2.fontSize,
                  lineHeight: theme.typography.body2.lineHeight,
                }}
              >
                ({percentage}%)
              </Text>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
  return (
    <Tooltip
      title={tooltipContent}
      followCursor
      slotProps={{
        popper: {
          modifiers: [
            {
              name: 'offset',
              options: { offset: [76, -12] },
            },
          ],
        },
        tooltip: {
          sx: {
            borderRadius: r('md'),
            border: `1px solid ${theme.app.colors.border}`,
            boxShadow: 'none',
            backgroundColor: theme.app.colors.surface,
            color: theme.app.colors.text,
            fontSize: theme.typography.body2.fontSize,
            lineHeight: theme.typography.body2.lineHeight,
            fontWeight: theme.typography.fontWeightRegular,
            paddingTop: '3px',
            paddingBottom: '3px',
            paddingLeft: '4px',
            paddingRight: '4px',
          },
        },
      }}
    >
      <Box sx={{ width: `${size}px`, height: `${size}px`, flexShrink: 0 }}>
        <MuiPieChart
          series={series}
          width={size}
          height={size}
          skipAnimation
          margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
          slotProps={{ tooltip: { trigger: 'none' } }}
          sx={{
            '& .MuiChartsLegend-root': {
              display: 'none',
            },
            '& .MuiPieArc-root': {
              stroke: 'none',
            },
          }}
        />
      </Box>
    </Tooltip>
  );
};
